import { useState, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { itemsApi, vendorsApi, customersApi, type ItemDto, type VendorDto, type CustomerDto } from "@/api/endpoints";

interface PickerProps<T> {
  value: T | null;
  onChange: (v: T | null) => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

function useDebouncedValue(value: string, delay = 300) {
  const [d, setD] = useState(value);
  useMemo(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);
  return d;
}

export function ItemPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<ItemDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["items-picker", debounced],
    queryFn: () => itemsApi.list({ search: debounced || undefined, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });
  return (
    <Autocomplete<ItemDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.sku} — ${o.name}${o.strength ? ` (${o.strength})` : ""}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}

export function VendorPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<VendorDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["vendors-picker", debounced],
    queryFn: () => vendorsApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 60_000,
  });
  return (
    <Autocomplete<VendorDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}

export function CustomerPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<CustomerDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["customers-picker", debounced],
    queryFn: () => customersApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });
  return (
    <Autocomplete<CustomerDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}${o.uhid ? ` [${o.uhid}]` : ""}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
